import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';

const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;
const OAUTH_GOOGLE_ENABLED = process.env.REACT_APP_OAUTH_GOOGLE === 'true';
const OAUTH_APPLE_ENABLED  = process.env.REACT_APP_OAUTH_APPLE  === 'true';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

const Login = () => {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const isPhone = useMediaQuery('(max-width: 640px)');

  const signInWithProvider = async (provider) => {
    if (
      (provider === 'google' && !OAUTH_GOOGLE_ENABLED) ||
      (provider === 'apple'  && !OAUTH_APPLE_ENABLED)
    ) return;

    setMsg('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: SITE_URL },
    });
    if (error) setMsg(error.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');

    if (mode === 'reset') {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${SITE_URL}/reset`,
      });
      if (error) setMsg(error.message);
      else setMsg('Reset email sent (if SMTP configured).');
      return;
    }

    if (mode === 'signup') {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: SITE_URL },
      });
      if (error) setMsg(error.message);
      else setMsg('Check your email to confirm your account (if confirmations are enabled).');
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) setMsg(error.message);
  };

  const colors = {
    bg: '#121212',
    card: '#1b1b1b',
    button: '#2a2a2a',
    border: '#3a3a3a',
    text: '#fff',
    sub: '#bbb',
  };

  const containerStyle = {
    minHeight: '100vh',
    background: colors.bg,
    color: colors.text,
    padding: isPhone ? 16 : 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0))',
  };

  const cardStyle = {
    width: '100%',
    maxWidth: isPhone ? 420 : 440,
    background: colors.card,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: '0 1px 0 rgba(255,255,255,0.04)',
    padding: isPhone ? 16 : 20,
    overflow: 'hidden',
  };

  const innerStyle = {
    maxWidth: isPhone ? '100%' : 360,
    margin: '0 auto',
  };

  const h1Style = {
    margin: '0 0 12px',
    fontSize: isPhone ? 22 : 26,
    textAlign: 'center',
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: 12,
    background: '#121212',
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    outline: 'none',
  };

  const primaryBtn = {
    width: '100%',
    boxSizing: 'border-box',
    padding: 12,
    background: colors.button,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    cursor: 'pointer',
  };

  const subtleBtn = {
    background: 'transparent',
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    padding: '10px 12px',
    cursor: 'pointer',
    boxSizing: 'border-box',
    width: '100%',
  };

  const oauthBtnStyle = (enabled) => ({
    ...subtleBtn,
    opacity: enabled ? 1 : 0.5,
    cursor: enabled ? 'pointer' : 'not-allowed',
    pointerEvents: enabled ? 'auto' : 'none',
  });

  const showAnyOAuth = OAUTH_GOOGLE_ENABLED || OAUTH_APPLE_ENABLED;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h1 style={h1Style}>
          {mode === 'signin' ? 'Sign in' : mode === 'signup' ? 'Create account' : 'Reset password'}
        </h1>

        <div style={innerStyle}>
          {showAnyOAuth && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => signInWithProvider('google')}
                  disabled={!OAUTH_GOOGLE_ENABLED}
                  aria-disabled={!OAUTH_GOOGLE_ENABLED}
                  title={OAUTH_GOOGLE_ENABLED ? 'Continue with Google' : 'Google sign-in disabled'}
                  style={oauthBtnStyle(OAUTH_GOOGLE_ENABLED)}
                >
                  Continue with Google
                </button>
                <button
                  type="button"
                  onClick={() => signInWithProvider('apple')}
                  disabled={!OAUTH_APPLE_ENABLED}
                  aria-disabled={!OAUTH_APPLE_ENABLED}
                  title={OAUTH_APPLE_ENABLED ? 'Continue with Apple' : 'Apple sign-in disabled'}
                  style={oauthBtnStyle(OAUTH_APPLE_ENABLED)}
                >
                  Continue with Apple
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0' }}>
                <div style={{ flex: 1, height: 1, background: colors.border }} />
                <div style={{ fontSize: 12, color: colors.sub }}>or use email</div>
                <div style={{ flex: 1, height: 1, background: colors.border }} />
              </div>
            </>
          )}

          <form method="post" onSubmit={handleSubmit} autoComplete="on">
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label htmlFor="email" style={{ display: 'block', marginBottom: 6, color: colors.sub, fontSize: 13 }}>
                  Email
                </label>
                <input
                  id="email"
                  name="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="username"
                  style={inputStyle}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {mode !== 'reset' && (
                <div>
                  <label htmlFor="password" style={{ display: 'block', marginBottom: 6, color: colors.sub, fontSize: 13 }}>
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    style={inputStyle}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={mode !== 'reset'}
                  />
                </div>
              )}

              {mode === 'signup' && (
                <button type="submit" style={primaryBtn}>Create account</button>
              )}
              {mode === 'signin' && (
                <button type="submit" style={primaryBtn} disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              )}
              {mode === 'reset' && (
                <button type="submit" style={primaryBtn}>Send reset link</button>
              )}
            </div>
          </form>

          {msg && (
            <p style={{ marginTop: 10, color: '#ccc' }}>{msg}</p>
          )}

          <div
            style={{
              marginTop: 14,
              display: 'grid',
              gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr 1fr',
              gap: 8,
            }}
          >
            {mode !== 'signin' && (
              <button type="button" onClick={() => setMode('signin')} style={subtleBtn}>
                Have an account? Sign in
              </button>
            )}
            {mode !== 'signup' && (
              <button type="button" onClick={() => setMode('signup')} style={subtleBtn}>
                New here? Sign up
              </button>
            )}
            {mode !== 'reset' && (
              <button type="button" onClick={() => setMode('reset')} style={subtleBtn}>
                Forgot password?
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
